import { catalog } from "./catalog.js";
const ANON_ID_STORAGE_KEY = "ferrivia.anon_id";
const SESSION_ID_STORAGE_KEY = "ferrivia.session_id";
const ATTRIBUTION_STORAGE_KEY = "ferrivia.attribution.v2";
const LEGACY_ATTRIBUTION_STORAGE_KEY = "ferrivia.attribution";
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const FLUSH_INTERVAL_MS = 5000;
const BATCH_CAP = 20;
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const INTERNAL_REFERRER_HOSTS = ["ferrivia.fi"];
// try/catch-wrapped the same way apps/*/src/api.ts guards localStorage —
// private browsing / disabled storage must degrade to "no persistent id",
// never throw. getStorage is itself inside the try: in Safari private mode,
// merely referencing window.localStorage can throw, not just its methods.
function readOrCreateId(getStorage, key) {
    try {
        const storage = getStorage();
        const existing = storage.getItem(key);
        if (existing) {
            return existing;
        }
        const id = globalThis.crypto.randomUUID();
        storage.setItem(key, id);
        return id;
    }
    catch {
        return undefined;
    }
}
// Read from window.location.search on tracker creation, not from getContext()
// on every track() call — the URL's marketing params are only ever present on
// the landing request, so a touch has to be captured at page load, not
// re-derived per event.
function captureTouch() {
    const params = new URLSearchParams(window.location.search);
    const touch = { landing_path: window.location.pathname, at: new Date().toISOString() };
    for (const key of UTM_PARAMS) {
        const value = params.get(key);
        if (value) {
            touch[key] = value;
        }
    }
    const gclid = params.get("gclid");
    if (gclid) {
        touch.gclid = gclid;
    }
    const fbclid = params.get("fbclid");
    if (fbclid) {
        touch.fbclid = fbclid;
    }
    if (document.referrer) {
        touch.referrer = document.referrer;
    }
    return touch;
}
export function isExternalReferrer(referrer) {
    if (!referrer) {
        return false;
    }
    try {
        const { hostname } = new URL(referrer);
        return !INTERNAL_REFERRER_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`));
    }
    catch {
        return false;
    }
}
// A touch only counts as a new channel if it carries a marketing signal or
// arrived from outside Ferrivia. Without this, every internal navigation
// would overwrite the real channel with a self-referral.
function isNonDirect(touch) {
    if (UTM_PARAMS.some((key) => touch[key]) || touch.gclid || touch.fbclid) {
        return true;
    }
    return isExternalReferrer(touch.referrer ?? "");
}
function isLive(record) {
    const firstAt = Date.parse(record.first.at);
    return Number.isFinite(firstAt) && Date.now() - firstAt <= ATTRIBUTION_TTL_MS;
}
// Persisted in localStorage, not sessionStorage: the ad or link that brought
// someone here has to survive closing the tab, because the order it leads to
// often lands days later. Bounded at ATTRIBUTION_TTL_MS from the *first*
// touch — a hard expiry, not a sliding one, so "up to 90 days" in the privacy
// copy is literally true.
function readOrCreateAttribution() {
    try {
        sessionStorage.removeItem(LEGACY_ATTRIBUTION_STORAGE_KEY);
    }
    catch {
        // Storage unavailable — nothing stale to clean up.
    }
    try {
        const touch = captureTouch();
        const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
        const existing = raw ? JSON.parse(raw) : undefined;
        if (existing?.first?.at && isLive(existing)) {
            const record = {
                version: 1,
                first: existing.first,
                last: isNonDirect(touch) ? touch : (existing.last ?? existing.first),
            };
            localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
            return record;
        }
        const record = { version: 1, first: touch, last: touch };
        localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
        return record;
    }
    catch {
        return undefined;
    }
}
// The flat first-touch keys are emitted exactly as before so rows already
// collected — and any query written against them — keep their meaning; the
// last touch rides alongside as a nested object.
function attributionContext(record) {
    if (!record) {
        return {};
    }
    const { at, ...first } = record.first;
    return { ...first, attribution_first_at: at, last_touch: record.last };
}
export function createTracker(options) {
    const { endpoint, source, getToken, getUserId, getContext } = options;
    const anonymousId = readOrCreateId(() => localStorage, ANON_ID_STORAGE_KEY);
    const sessionId = readOrCreateId(() => sessionStorage, SESSION_ID_STORAGE_KEY);
    const attribution = attributionContext(readOrCreateAttribution());
    function buildContext() {
        const merged = { ...attribution, ...getContext?.() };
        return Object.keys(merged).length > 0 ? merged : undefined;
    }
    let queue = [];
    let timer;
    function scheduleFlush() {
        if (timer !== undefined) {
            return;
        }
        timer = setTimeout(() => {
            timer = undefined;
            flush(false);
        }, FLUSH_INTERVAL_MS);
    }
    function flush(useBeacon) {
        if (queue.length === 0) {
            return;
        }
        const batch = queue;
        queue = [];
        if (timer !== undefined) {
            clearTimeout(timer);
            timer = undefined;
        }
        try {
            const body = JSON.stringify(batch);
            if (useBeacon) {
                // sendBeacon can't set custom headers, so the source signal has to
                // travel some other way that survives a cross-origin POST — a query
                // param on the endpoint URL, which the collector validates against
                // the same fixed enum as the header below (falling back to Origin
                // if neither is a recognised value).
                const beaconUrl = new URL(endpoint);
                beaconUrl.searchParams.set("source", source);
                navigator.sendBeacon(beaconUrl.href, new Blob([body], { type: "application/json" }));
                return;
            }
            const headers = {
                "Content-Type": "application/json",
                "X-Ferrivia-Source": source,
            };
            // Only attribute the request to a token if a user is actually signed
            // in — getToken() alone doesn't distinguish a stale/leftover token
            // from a live session.
            const token = getUserId?.() ? getToken?.() : undefined;
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            void fetch(endpoint, { method: "POST", headers, body, keepalive: true }).catch(() => { });
        }
        catch {
            // Analytics must never throw or break the app it's instrumenting.
        }
    }
    try {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                flush(true);
            }
        });
        window.addEventListener("pagehide", () => flush(true));
    }
    catch {
        // Non-browser or restricted environment — timer/cap flush still works.
    }
    function track(name, properties) {
        try {
            const validated = catalog[name].parse(properties);
            queue.push({
                event_name: name,
                occurred_at: new Date().toISOString(),
                properties: validated,
                session_id: sessionId,
                anonymous_id: anonymousId,
                context: buildContext(),
            });
            if (queue.length >= BATCH_CAP) {
                flush(false);
            }
            else {
                scheduleFlush();
            }
        }
        catch {
            // Fails silently — analytics must never break the app.
        }
    }
    return { track, sessionId };
}
